import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Alert } from '../BossUI';

const API_URL = process.env.REACT_APP_API_URL;

// Formats a JS Date as 'YYYY-MM-DDTHH:MM:SS' (no milliseconds/timezone
// suffix) -- the shape routes/surveys.py's _parse_value expects for
// datetime.fromisoformat() on Python 3.10.
export const toApiDatetime = (date) => date.toISOString().slice(0, 19);

// Shared shell for the public, no-login survey pages: validates the token
// on mount, then renders one of loading / invalid-link / form / thank-you.
// `surveyType` is 'initial' or 'final'; `title` is shown in the header;
// `renderForm` is called with { studentFirstName, startTime, submit } once
// the token is confirmed valid, where submit(payload) POSTs the survey.
const PublicSurveyPage = ({ surveyType, title, renderForm }) => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | invalid | ready | submitted | error
  const [studentFirstName, setStudentFirstName] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetch(`${API_URL}/surveys/${surveyType}/token/${token}`)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setStatus('invalid');
          return;
        }
        const data = await res.json();
        setStudentFirstName(data.studentFirstName || null);
        setStartTime(new Date());
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('invalid');
      });

    return () => { cancelled = true; };
  }, [surveyType, token]);

  const submit = async (fieldValues) => {
    setSubmitError(null);
    try {
      const response = await fetch(`${API_URL}/surveys/${surveyType}/token/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...fieldValues,
          start_time: toApiDatetime(startTime),
          completion_time: toApiDatetime(new Date()),
        }),
      });
      if (!response.ok) {
        if (response.status === 404) {
          setStatus('invalid');
        } else {
          setSubmitError('Something went wrong submitting your survey. Please try again.');
        }
        return;
      }
      setStatus('submitted');
    } catch (e) {
      setSubmitError('Something went wrong submitting your survey. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <img src="/hh.jpg" alt="Warrior Music Foundation" className="h-10 w-10 rounded-lg" />
          <h1 className="ml-3 text-lg font-bold text-neutral-900">{title}</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {status === 'loading' && (
          <Card className="p-8 text-center text-neutral-500">Loading survey…</Card>
        )}

        {status === 'invalid' && (
          <Card className="p-8">
            <Alert variant="error" title="This link is no longer valid">
              This survey link is invalid, has expired, or has already been used. If you believe
              this is a mistake, please contact your Warrior Music Foundation program coordinator
              for a new link.
            </Alert>
          </Card>
        )}

        {status === 'submitted' && (
          <Card className="p-8">
            <Alert variant="success" title="Thank you!">
              Your response has been recorded. You may now close this page.
            </Alert>
          </Card>
        )}

        {status === 'ready' && (
          <Card className="p-6 sm:p-8">
            {studentFirstName && (
              <p className="text-sm text-neutral-500 mb-4">Hi {studentFirstName}, thanks for taking a moment to fill this out.</p>
            )}
            {submitError && (
              <div className="mb-4">
                <Alert variant="error">{submitError}</Alert>
              </div>
            )}
            {renderForm({ studentFirstName, startTime, submit })}
          </Card>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-xs text-neutral-500">
        Warrior Music Foundation
      </footer>
    </div>
  );
};

export default PublicSurveyPage;
